/* ═══════════════════════════════════════════════════════════════════════════
   ☎️ callLogSync — one place two very different call logs can agree on.

   ── 🟥 WHY DELETING ON THE PHONE NEVER REACHED THE BROWSER ──────────────────
   There is no shared call log. There never was. The two clients keep entirely
   separate, purely LOCAL stores:

     • the app  → SQLite `call_history` message rows (services/MessageDB)
     • the web  → localStorage['allo_call_feed'], fed by the server's
                  `call-history-sync` socket broadcast, plus the phone's export

   Firestore holds nothing durable: the per-call relay doc in
   chats/{chatId}/messages is STORE-AND-FORWARD — consumed and deleted the
   moment a phone ingests it. So the app's delete could only ever touch its own
   SQLite and a cloud doc that had usually already been swept. There was no
   shared thing to delete, which is why nothing synced in either direction.

   This module is that shared thing. Not the log itself — the log stays local
   and fast on both sides — but a small DELETION LEDGER both clients read:

     users/{uid}/meta/callLog                → { clearedAt }
     users/{uid}/callLogDeletions/{sessionKey} → { at }

   ── 🟥 "CLEAR ALL" IS A WATERMARK, NOT N TOMBSTONES ────────────────────────
   Writing one document per call would mean thousands of writes for somebody who
   has been using Allo for a year, on a button labelled "clear log". `clearedAt`
   is a single field that hides everything older than the moment it was pressed
   — and it keeps working for records that arrive LATE, which individual
   tombstones cannot: a call the phone exports tomorrow but which happened last
   week is still older than the watermark, so it stays gone.

   ── 🟥 ONE KEY, NORMALISED IN ONE PLACE ────────────────────────────────────
   The app keys calls on `callData.callSessionId` and names the message
   `call_end_<sessionId>`; the web keys on `callSessionId || callId` and strips
   the same prefix. If one side ever writes `call_end_abc` where the other looks
   up `abc`, the ledger silently matches nothing and every delete appears to
   work and then comes back. `sessionKeyOf` is the only place that decision is
   made, and both clients call it — that is the whole reason it is exported
   rather than inlined twice.
   ═══════════════════════════════════════════════════════════════════════════ */

import {
  collection, doc, getDocs, onSnapshot, setDoc, writeBatch, deleteDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';

/** Firestore caps a batch at 500 operations. */
const BATCH_LIMIT = 450;

export const callLogMetaRef = (uid) => doc(db, 'users', String(uid), 'meta', 'callLog');
export const callLogTombsRef = (uid) => collection(db, 'users', String(uid), 'callLogDeletions');

/**
 * The one key both clients agree on.
 *
 * 🟥 A DOCUMENT ID CANNOT CONTAIN '/' AND CANNOT BE '.' OR '..'. Session ids are
 *    generated, not typed, so this is unlikely — but a single bad id would throw
 *    inside a batch and take every other deletion in it down with it.
 */
export function sessionKeyOf(x) {
  const raw = String((x && (x.callSessionId || x.callId || x.id)) || x || '').trim();
  const bare = raw.replace(/^call_end_/, '');
  if (!bare || bare === '.' || bare === '..') return '';
  return bare.replace(/\//g, '_');
}

/** The empty ledger — nothing hidden. */
export const EMPTY_LEDGER = { clearedAt: 0, ids: new Set() };

/**
 * Should this row be hidden?
 *
 * 🟥 `ts <= clearedAt`, NOT `<`. Two calls can land in the same millisecond as
 *    the clear, and "clear all" that leaves one row behind reads as broken.
 *
 * 🟢 A row with NO timestamp (0) is hidden by any clear-all. That is deliberate:
 *    an undatable record is old, and "clear the log" should clear it rather than
 *    leave one mystery row that nothing can ever remove.
 */
export function isCallHidden(ledger, key, ts) {
  if (!ledger) return false;
  const k = sessionKeyOf(key);
  if (k && ledger.ids && ledger.ids.has(k)) return true;
  if (!ledger.clearedAt) return false;
  return (Number(ts) || 0) <= ledger.clearedAt;
}

/** Hide specific calls, on every device. */
export async function hideCalls(uid, keys) {
  const list = [...new Set((keys || []).map(sessionKeyOf).filter(Boolean))];
  if (!uid || !list.length) return 0;

  const at = Date.now();
  for (let i = 0; i < list.length; i += BATCH_LIMIT) {
    const batch = writeBatch(db);
    for (const k of list.slice(i, i + BATCH_LIMIT)) {
      batch.set(doc(callLogTombsRef(uid), k), { at });
    }
    // eslint-disable-next-line no-await-in-loop
    await batch.commit();
  }
  return list.length;
}

/**
 * Hide everything up to now.
 *
 * 🟢 THE OLD TOMBSTONES ARE SWEPT AFTERWARDS, not before. Each one is now
 *    redundant — the watermark already covers it — and leaving them would grow
 *    a collection nothing ever prunes. Doing it after the watermark lands means
 *    a failure here loses nothing: the calls stay hidden either way.
 */
export async function clearAllCalls(uid, at = Date.now()) {
  if (!uid) return 0;
  await setDoc(callLogMetaRef(uid), { clearedAt: at }, { merge: true });

  try {
    const snap = await getDocs(callLogTombsRef(uid));
    const stale = snap.docs.filter((d) => (Number(d.data()?.at) || 0) <= at);
    for (let i = 0; i < stale.length; i += BATCH_LIMIT) {
      const batch = writeBatch(db);
      for (const d of stale.slice(i, i + BATCH_LIMIT)) batch.delete(d.ref);
      // eslint-disable-next-line no-await-in-loop
      await batch.commit();
    }
  } catch { /* the watermark is what matters; pruning is housekeeping */ }
  return at;
}

/** Un-hide one call — there is no UI for it, but a delete you cannot reverse is a trap. */
export async function unhideCall(uid, key) {
  const k = sessionKeyOf(key);
  if (!uid || !k) return;
  await deleteDoc(doc(callLogTombsRef(uid), k)).catch(() => {});
}

/**
 * Watch the ledger. Calls back with { clearedAt, ids } whenever it changes.
 *
 * 🟥 BOTH HALVES ARE WATCHED, AND EITHER CAN ARRIVE FIRST. Emitting only when
 *    both have reported would leave the list unfiltered on a cold load until the
 *    slower one landed — which is a flash of deleted calls. Emitting on each
 *    means the filter tightens as the data arrives, never loosens.
 */
export function subscribeCallLogDeletions(uid, cb) {
  if (!uid) { cb(EMPTY_LEDGER); return () => {}; }

  const state = { clearedAt: 0, ids: new Set() };
  const emit = () => cb({ clearedAt: state.clearedAt, ids: new Set(state.ids) });

  const offMeta = onSnapshot(
    callLogMetaRef(uid),
    (snap) => { state.clearedAt = Number(snap.data()?.clearedAt) || 0; emit(); },
    () => {},
  );
  const offTombs = onSnapshot(
    callLogTombsRef(uid),
    (snap) => { state.ids = new Set(snap.docs.map((d) => d.id)); emit(); },
    () => {},
  );

  return () => { offMeta(); offTombs(); };
}
